import { useClerk } from '@clerk/shared/react';
import type { SignInFactor } from '@clerk/shared/types';
import React from 'react';

import { useCardState, withCardStateProvider } from '@/ui/elements/contexts';
import { ErrorCard } from '@/ui/elements/ErrorCard';
import { LoadingCard } from '@/ui/elements/LoadingCard';

import { withRedirectToAfterSignIn, withRedirectToSignInTask } from '../../common';
import { useCoreSignIn, useEnvironment } from '../../contexts';
import { useAlternativeStrategies } from '../../hooks/useAlternativeStrategies';
import { localizationKeys } from '../../localization';
import { useRouter } from '../../router';
import type { AlternativeMethodsMode } from './AlternativeMethods';
import { AlternativeMethods } from './AlternativeMethods';
import { getSSOBypassFactor, hasMultipleEnterpriseConnections, SIGN_IN_RESET_PASSWORD_INTENT_PARAM } from './shared';
import { SignInFactorOneAlternativePhoneCodeCard } from './SignInFactorOneAlternativePhoneCodeCard';
import { SignInFactorOneEmailCodeCard } from './SignInFactorOneEmailCodeCard';
import { SignInFactorOneEmailLinkCard } from './SignInFactorOneEmailLinkCard';
import { SignInFactorOneEnterpriseConnections } from './SignInFactorOneEnterpriseConnections';
import { SignInFactorOneForgotPasswordCard } from './SignInFactorOneForgotPasswordCard';
import { SignInFactorOnePasskey } from './SignInFactorOnePasskey';
import type { PasswordErrorCode } from './SignInFactorOnePasswordCard';
import { SignInFactorOnePasswordCard } from './SignInFactorOnePasswordCard';
import { SignInFactorOnePhoneCodeCard } from './SignInFactorOnePhoneCodeCard';
import { SignInFactorOneSSOBypass } from './SignInFactorOneSSOBypass';
import { useResetPasswordFactor } from './useResetPasswordFactor';
import { determineStartingSignInFactor, factorHasLocalStrategy } from './utils';

const factorKey = (factor: SignInFactor | null | undefined) => {
  if (!factor) {
    return '';
  }
  let key = factor.strategy;
  if ('emailAddressId' in factor) {
    key += factor.emailAddressId;
  }
  if ('phoneNumberId' in factor) {
    key += factor.phoneNumberId;
  }
  if ('channel' in factor) {
    key += factor.channel;
  }
  return key;
};

function determineAlternativeMethodsMode(
  showForgotPasswordStrategies: boolean,
  passwordErrorCode: PasswordErrorCode | null,
): AlternativeMethodsMode {
  if (!showForgotPasswordStrategies) {
    return 'default';
  }

  if (passwordErrorCode === 'pwned') {
    return 'pwned';
  }

  if (passwordErrorCode === 'compromised') {
    return 'passwordCompromised';
  }

  return 'forgot';
}

function removeSignInResetPasswordIntentParam(): void {
  if (typeof window === 'undefined') {
    return;
  }

  const url = new URL(window.location.href);
  if (url.searchParams.has(SIGN_IN_RESET_PASSWORD_INTENT_PARAM)) {
    url.searchParams.delete(SIGN_IN_RESET_PASSWORD_INTENT_PARAM);
    window.history.replaceState(window.history.state, '', url);
  }
}

function SignInFactorOneInternal(): JSX.Element {
  const { __internal_setActiveInProgress } = useClerk();
  const signIn = useCoreSignIn();
  const { preferredSignInStrategy } = useEnvironment().displayConfig;
  const availableFactors = signIn.supportedFirstFactors;
  const router = useRouter();
  const card = useCardState();
  const { supportedFirstFactors, firstFactorVerification } = useCoreSignIn();

  const lastPreparedFactorKeyRef = React.useRef('');
  const [{ currentFactor }, setFactor] = React.useState<{
    currentFactor: SignInFactor | undefined | null;
    prevCurrentFactor: SignInFactor | undefined | null;
  }>(() => {
    const factor = determineStartingSignInFactor(availableFactors, signIn.identifier, preferredSignInStrategy);
    if (
      factor?.strategy === 'phone_code' &&
      !!firstFactorVerification.channel &&
      firstFactorVerification.channel !== 'sms'
    ) {
      // This is only applied to phone_code with channel that is not 'sms'
      // because we don't want to send the channel parameter when its value is 'sms'
      factor.channel = firstFactorVerification.channel;
    }
    return {
      currentFactor: factor,
      prevCurrentFactor: undefined,
    };
  });

  const { hasAnyStrategy } = useAlternativeStrategies({
    filterOutFactor: currentFactor,
    supportedFirstFactors,
  });

  // Frozen on mount: client-piggybacking may drop the field, which would otherwise unmount the
  // fallback screens mid-flow.
  const ssoBypassFactor = React.useRef(getSSOBypassFactor(signIn)).current;

  const resetPasswordFactor = useResetPasswordFactor();
  const resetPasswordIntent = router.queryParams[SIGN_IN_RESET_PASSWORD_INTENT_PARAM] === 'true';

  const [showAllStrategies, setShowAllStrategies] = React.useState<boolean>(() => {
    const defaultShow = !currentFactor || !factorHasLocalStrategy(currentFactor);
    return defaultShow || (resetPasswordIntent && !resetPasswordFactor);
  });

  const [showForgotPasswordStrategies, setShowForgotPasswordStrategies] = React.useState(
    () => resetPasswordIntent && !!resetPasswordFactor,
  );

  const [passwordErrorCode, setPasswordErrorCode] = React.useState<PasswordErrorCode | null>(null);

  React.useEffect(() => {
    if (__internal_setActiveInProgress) {
      return;
    }

    // Handle the case where a user lands on alternative methods screen,
    // clicks a social button but then navigates back to sign in.
    // SignIn status resets to 'needs_identifier'
    if (signIn.status === 'needs_identifier' || signIn.status === null) {
      void router.navigate('../');
    }
  }, [__internal_setActiveInProgress]);

  if (!currentFactor) {
    return signIn.status ? (
      <ErrorCard
        cardTitle={localizationKeys('signIn.noAvailableMethods.title')}
        cardSubtitle={localizationKeys('signIn.noAvailableMethods.subtitle')}
        message={localizationKeys('signIn.noAvailableMethods.message')}
      />
    ) : (
      <LoadingCard />
    );
  }

  const toggleAllStrategies = hasAnyStrategy ? () => setShowAllStrategies(s => !s) : undefined;

  const toggleForgotPasswordStrategies = () => setShowForgotPasswordStrategies(s => !s);

  const handleFactorPrepare = () => {
    lastPreparedFactorKeyRef.current = factorKey(currentFactor);
  };
  const selectFactor = (factor: SignInFactor) => {
    setFactor(prev => ({
      currentFactor: factor,
      prevCurrentFactor: prev.currentFactor,
    }));
  };

  if (ssoBypassFactor) {
    return <SignInFactorOneSSOBypass bypassFactor={ssoBypassFactor} />;
  }

  /**
   * Prompt to choose between a list of enterprise connections as supported first factors
   * @experimental
   */
  if (hasMultipleEnterpriseConnections(signIn.supportedFirstFactors)) {
    return <SignInFactorOneEnterpriseConnections />;
  }

  if (showAllStrategies || showForgotPasswordStrategies) {
    // Password errors are not recoverable by re-entering the password, so we hide the back button
    const canGoBack = factorHasLocalStrategy(currentFactor) && !passwordErrorCode;

    const toggle = showAllStrategies ? toggleAllStrategies : toggleForgotPasswordStrategies;
    const leaveAlternativeMethods = () => {
      // This search param only exists if the user clicked "Forgot password?" on the
      // start page, it's a way to go directly to the password reset screen.
      // If it does exist, we want to remove it on exit so refresh works correctly after.
      removeSignInResetPasswordIntentParam();
      toggle?.();
    };
    const backHandler: React.MouseEventHandler<Element> = () => {
      card.setError(undefined);
      setPasswordErrorCode(null);
      leaveAlternativeMethods();
    };

    const mode = determineAlternativeMethodsMode(showForgotPasswordStrategies, passwordErrorCode);

    return (
      <AlternativeMethods
        mode={mode}
        onBackLinkClick={canGoBack ? backHandler : undefined}
        onFactorSelected={f => {
          selectFactor(f);
          leaveAlternativeMethods();
        }}
        currentFactor={currentFactor}
      />
    );
  }

  if (!currentFactor) {
    return <LoadingCard />;
  }

  switch (currentFactor?.strategy) {
    case 'passkey':
      return (
        <SignInFactorOnePasskey
          onFactorPrepare={handleFactorPrepare}
          onShowAlternativeMethodsClick={toggleAllStrategies}
        />
      );
    case 'password':
      return (
        <SignInFactorOnePasswordCard
          onForgotPasswordMethodClick={resetPasswordFactor ? toggleForgotPasswordStrategies : toggleAllStrategies}
          onShowAlternativeMethodsClick={toggleAllStrategies}
          onPasswordError={errorCode => {
            setPasswordErrorCode(errorCode);
            toggleForgotPasswordStrategies();
          }}
        />
      );
    case 'email_code':
      return (
        <SignInFactorOneEmailCodeCard
          factorAlreadyPrepared={lastPreparedFactorKeyRef.current === factorKey(currentFactor)}
          onFactorPrepare={handleFactorPrepare}
          factor={currentFactor}
          onShowAlternativeMethodsClicked={toggleAllStrategies}
        />
      );
    case 'phone_code':
      if (currentFactor.channel && currentFactor.channel !== 'sms') {
        // Alternative phone code provider (e.g. WhatsApp)
        return (
          <SignInFactorOneAlternativePhoneCodeCard
            factorAlreadyPrepared={lastPreparedFactorKeyRef.current === factorKey(currentFactor)}
            onFactorPrepare={handleFactorPrepare}
            factor={currentFactor}
            onChangePhoneCodeChannel={selectFactor}
          />
        );
      } else {
        // SMS
        return (
          <SignInFactorOnePhoneCodeCard
            factorAlreadyPrepared={lastPreparedFactorKeyRef.current === factorKey(currentFactor)}
            onFactorPrepare={handleFactorPrepare}
            factor={currentFactor}
            onShowAlternativeMethodsClicked={toggleAllStrategies}
          />
        );
      }

    case 'email_link':
      return (
        <SignInFactorOneEmailLinkCard
          factorAlreadyPrepared={lastPreparedFactorKeyRef.current === factorKey(currentFactor)}
          onFactorPrepare={handleFactorPrepare}
          factor={currentFactor}
          onShowAlternativeMethodsClicked={toggleAllStrategies}
        />
      );
    case 'reset_password_phone_code':
      return (
        <SignInFactorOneForgotPasswordCard
          factorAlreadyPrepared={lastPreparedFactorKeyRef.current === factorKey(currentFactor)}
          onFactorPrepare={handleFactorPrepare}
          factor={currentFactor}
          onShowAlternativeMethodsClicked={toggleAllStrategies}
          onBackLinkClicked={() => {
            setFactor(prev => ({
              currentFactor: prev.prevCurrentFactor,
              prevCurrentFactor: prev.currentFactor,
            }));
            toggleForgotPasswordStrategies();
          }}
          cardSubtitle={localizationKeys('signIn.forgotPassword.subtitle_phone')}
        />
      );

    case 'reset_password_email_code':
      return (
        <SignInFactorOneForgotPasswordCard
          factorAlreadyPrepared={lastPreparedFactorKeyRef.current === factorKey(currentFactor)}
          onFactorPrepare={handleFactorPrepare}
          factor={currentFactor}
          onShowAlternativeMethodsClicked={toggleAllStrategies}
          onBackLinkClicked={() => {
            setFactor(prev => ({
              currentFactor: prev.prevCurrentFactor,
              prevCurrentFactor: prev.currentFactor,
            }));
            toggleForgotPasswordStrategies();
          }}
          cardSubtitle={localizationKeys('signIn.forgotPassword.subtitle_email')}
        />
      );
    default:
      return <LoadingCard />;
  }
}

export const SignInFactorOne = withRedirectToSignInTask(
  withRedirectToAfterSignIn(withCardStateProvider(SignInFactorOneInternal)),
);
